import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import { TimerOutlined as TimerOutlinedIcon } from "../icons";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { ArrowForward as ArrowForwardIcon } from "../icons";
import { T } from "../routes/Translate";
import { brandColors } from "../theme";

// Fechas de los eventos de descuentos online de Argentina, en hora de Buenos
// Aires (-03:00). `end` es exclusivo: el evento termina a la medianoche de ese
// día. Sin `start` la fecha todavía no está confirmada (la CACE la anuncia
// unos meses antes) y se muestra sin reloj. Al pasar un evento, agregar la
// edición siguiente acá.
const SALE_EVENTS = [
  {
    name: "CyberMonday",
    start: "2026-11-02T00:00:00-03:00",
    end: "2026-11-05T00:00:00-03:00",
    when: "2, 3 y 4 de noviembre",
  },
  {
    name: "Black Friday",
    start: "2026-11-27T00:00:00-03:00",
    end: "2026-11-28T00:00:00-03:00",
    when: "27 de noviembre",
  },
  {
    name: "Hot Sale",
    when: "mayo 2027 · fecha a confirmar",
  },
];

const pad = (n) => String(n).padStart(2, "0");
const formatCountdown = (ms) => {
  const total = Math.max(Math.floor(ms / 1000), 0);
  const days = Math.floor(total / 86400);
  const time = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  return days > 0 ? `${days}d ${time}` : time;
};

const timeOf = (iso) => (iso ? new Date(iso).getTime() : null);

// Banner arriba del feed de ofertas: cuenta regresiva al próximo evento
// ("Empieza en") o, mientras dura, al final ("Termina en"), y debajo los que
// vienen después, y el acceso a /precios (los precios que guardamos para
// comparar en esos eventos). Las etiquetas van en <T> y los números afuera, para no
// retraducir cada segundo (igual que EventCountdown).
const SaleEventsCountdown = () => {
  const [now, setNow] = useState(() => Date.now());

  const pending = SALE_EVENTS.filter((e) => !e.end || timeOf(e.end) > now);
  const current = pending.find((e) => e.start);
  const hasClock = Boolean(current);

  useEffect(() => {
    if (!hasClock) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasClock]);

  if (pending.length === 0) return null;

  const live = current && now >= timeOf(current.start);
  const remaining = current ? (live ? timeOf(current.end) : timeOf(current.start)) - now : null;
  const others = pending.filter((e) => e !== current);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 3,
        borderColor: live ? "success.main" : brandColors.accent,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      {current && (
        <>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
          <LocalOfferIcon sx={{ fontSize: 20, alignSelf: "center", color: live ? "success.main" : brandColors.accent }} />
          <Typography sx={{ fontWeight: 800 }}>{current.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <T>{current.when}</T>
          </Typography>
        </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: live ? "success.main" : remaining <= 24 * 60 * 60 * 1000 ? "error.main" : "warning.main",
            }}
          >
            <TimerOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
              {live ? <T>¡Está en curso! Termina en</T> : <T>Empieza en</T>}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.95rem", color: "inherit" }}
            >
              {formatCountdown(remaining)}
            </Typography>
          </Box>
        </>
      )}
      {others.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {current ? <T>Después:</T> : <T>Próximo:</T>}{" "}
          {others.map((e, i) => (
            <React.Fragment key={e.name}>
              {i > 0 && " · "}
              <strong>{e.name}</strong> <T>{e.when}</T>
            </React.Fragment>
          ))}
        </Typography>
      )}
      <Button
        component={RouterLink}
        to="/precios"
        size="small"
        endIcon={<ArrowForwardIcon />}
        sx={{ alignSelf: "flex-start", px: 0, mt: 0.5, textTransform: "none", fontWeight: 700 }}
      >
        <T>Ver los precios que seguimos</T>
      </Button>
    </Paper>
  );
};

export default SaleEventsCountdown;
